import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { KbdShortcut } from '../KbdShortcut/KbdShortcut';

/**
 * Global shortcut help dialog. Opens on `?` press (when no input focused).
 * Lists every shortcut grouped by category. Mounted once in Layout.
 *
 * Shortcuts are registered globally — extend SHORTCUT_GROUPS below or pass
 * `extraGroups` to layer page-specific shortcuts in.
 */
const SHORTCUT_GROUPS = [
  {
    title: 'Navigation',
    items: [
      { keys: 'cmd+k',  label: 'Open search' },
      { keys: 'g h',    label: 'Go to Dashboard' },
      { keys: 'g p',    label: 'Go to Procurement' },
      { keys: 'g i',    label: 'Go to Inventory' },
      { keys: 'g r',    label: 'Go to Retail' },
      { keys: 'g f',    label: 'Go to Finance' },
      { keys: 'g a',    label: 'Go to Action Queue' },
    ],
  },
  {
    title: 'On a list page',
    items: [
      { keys: 'n',      label: 'New item' },
      { keys: '/',      label: 'Focus search box' },
      { keys: 'r',      label: 'Refresh list' },
    ],
  },
  {
    title: 'Anywhere',
    items: [
      { keys: '?',      label: 'Show this help' },
      { keys: 'esc',    label: 'Close any dialog' },
    ],
  },
];

export function ShortcutHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      // Open on `?` (which is Shift+/ on US keyboards). Skip if user is typing
      // in an input — we don't want to steal the `?` they're trying to enter.
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      const editable = target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
      if (editable) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Keyboard shortcuts</Typography>
          <Typography variant="caption" color="text.secondary">Press <KbdShortcut keys="?" /> any time to open this</Typography>
        </Box>
        <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {SHORTCUT_GROUPS.map(group => (
          <Box key={group.title} sx={{ mb: 2.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}>
              {group.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {group.items.map(item => (
                <Box key={item.keys} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{item.label}</Typography>
                  <KbdShortcut keys={item.keys} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export default ShortcutHelp;
